import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash2, FileText, Paperclip, Save, X, Package, Loader2, Edit3, Download,
} from "lucide-react";

const EMPTY_PRODUCT = {
  name: "",
  manufacturer: "",
  usage: "",
  dosage: "",
  composition: "",
  risks: "",
  epis: "",
  first_aid: "",
  storage: "",
  expiry_date: "",
  observations: "",
  pdf_url: "",
  pdf_name: "",
};

const CAN_MANAGE_ROLES = ["superadmin", "admin", "supervisor"];

export default function CleaningProductsModal({ open, onOpenChange, venueId, user }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view; object = edit form
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canManage = user && CAN_MANAGE_ROLES.includes(user.role);

  const loadProducts = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const items = await hosys.entities.CleaningProduct.filter({ venue_id: venueId }, "-created_at");
      setProducts(items || []);
    } catch (err) {
      console.error("Error loading cleaning products:", err);
      toast.error("Error carregant productes");
    }
    setLoading(false);
  }, [venueId]);

  useEffect(() => {
    if (open) loadProducts();
  }, [open, loadProducts]);

  const handleOpenNew = () => setEditing({ ...EMPTY_PRODUCT });
  const handleOpenEdit = (p) => setEditing({ ...EMPTY_PRODUCT, ...p });
  const handleCancelEdit = () => setEditing(null);

  const handleUploadPDF = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Només es permeten fitxers PDF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El fitxer no pot superar 5 MB");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target.result.split(",")[1];
          const res = await hosys.functions.invoke("uploadFile", {
            file_name: file.name,
            file_base64: base64,
            file_type: "application/pdf",
          });
          const fileUrl = res?.data?.file_url || res?.file_url;
          if (fileUrl) {
            setEditing((prev) => ({ ...prev, pdf_url: fileUrl, pdf_name: file.name }));
            toast.success("PDF adjuntat");
          } else {
            toast.error("Error pujant el PDF");
          }
        } catch (err) {
          console.error(err);
          toast.error("Error pujant el PDF");
        }
        setUploading(false);
      };
      reader.onerror = () => {
        toast.error("Error llegint el fitxer");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      toast.error("Error pujant el PDF");
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editing?.name?.trim()) {
      toast.error("El nom del producte és obligatori");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, venue_id: venueId };
      // Drop "id" field from payload when updating (it's derived)
      const idToUpdate = editing.cleaning_product_id || editing.id;
      delete payload.id;

      if (idToUpdate) {
        await hosys.entities.CleaningProduct.update(idToUpdate, payload);
        toast.success("Producte actualitzat");
      } else {
        await hosys.entities.CleaningProduct.create(payload);
        toast.success("Producte creat");
      }
      setEditing(null);
      await loadProducts();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Error desant el producte");
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await hosys.entities.CleaningProduct.delete(deleteTarget.cleaning_product_id || deleteTarget.id);
      toast.success("Producte eliminat");
      setDeleteTarget(null);
      await loadProducts();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Error eliminant el producte");
    }
  };

  const updateField = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setEditing((prev) => ({ ...prev, [field]: value }));
  };

  const renderList = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {products.length} {products.length === 1 ? "fitxa registrada" : "fitxes registrades"}
        </p>
        {canManage && (
          <Button
            onClick={handleOpenNew}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
            data-testid="new-cleaning-product-btn"
          >
            <Plus className="w-4 h-4" /> Nova fitxa
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#7B2D3F]" />
        </div>
      ) : products.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <Package className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600 mb-1">Encara no hi ha fitxes de productes</p>
          <p className="text-xs text-gray-500">
            {canManage ? "Crea la primera fitxa per catalogar els productes de neteja utilitzats." : "Contacta amb un responsable per afegir fitxes."}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {products.map((p) => {
            const pid = p.cleaning_product_id || p.id;
            return (
              <Card key={pid} className="p-4 border-0 shadow-sm" data-testid={`product-card-${pid}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{p.name}</h4>
                      {p.manufacturer && (
                        <Badge variant="outline" className="text-xs">{p.manufacturer}</Badge>
                      )}
                      {p.pdf_url && (
                        <Badge className="text-xs bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <Paperclip className="w-3 h-3 mr-1" /> PDF
                        </Badge>
                      )}
                    </div>
                    {p.usage && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{p.usage}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                      {p.dosage && <span><strong className="text-gray-700">Dosi:</strong> {p.dosage}</span>}
                      {p.expiry_date && <span><strong className="text-gray-700">Caducitat:</strong> {p.expiry_date}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {p.pdf_url && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${p.pdf_url}`, "_blank")}
                        data-testid={`product-pdf-${pid}`}
                        title="Descarregar PDF"
                      >
                        <Download className="w-4 h-4 text-gray-600" />
                      </Button>
                    )}
                    {canManage && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenEdit(p)}
                          data-testid={`product-edit-${pid}`}
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4 text-gray-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteTarget(p)}
                          data-testid={`product-delete-${pid}`}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p-name">Nom del producte *</Label>
          <Input id="p-name" value={editing.name} onChange={updateField("name")} data-testid="product-name-input" placeholder="Ex: Desinfectant multi-superfícies" />
        </div>
        <div>
          <Label htmlFor="p-manufacturer">Fabricant / marca</Label>
          <Input id="p-manufacturer" value={editing.manufacturer} onChange={updateField("manufacturer")} placeholder="Ex: Proquimia" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p-usage">Ús / aplicació</Label>
          <Textarea id="p-usage" rows={2} value={editing.usage} onChange={updateField("usage")} placeholder="Superfícies de cuina, taules, etc." />
        </div>
        <div>
          <Label htmlFor="p-dosage">Dosi recomanada</Label>
          <Textarea id="p-dosage" rows={2} value={editing.dosage} onChange={updateField("dosage")} placeholder="10 ml / L d'aigua" />
        </div>
      </div>

      <div>
        <Label htmlFor="p-composition">Composició</Label>
        <Textarea id="p-composition" rows={2} value={editing.composition} onChange={updateField("composition")} placeholder="Tensioactius no iònics, quaternaris d'amoni..." />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p-risks">Riscos</Label>
          <Textarea id="p-risks" rows={2} value={editing.risks} onChange={updateField("risks")} placeholder="Irritant cutani, ocular..." />
        </div>
        <div>
          <Label htmlFor="p-epis">EPIs obligatoris</Label>
          <Textarea id="p-epis" rows={2} value={editing.epis} onChange={updateField("epis")} placeholder="Guants, ulleres de protecció..." />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p-first-aid">Primers auxilis</Label>
          <Textarea id="p-first-aid" rows={2} value={editing.first_aid} onChange={updateField("first_aid")} placeholder="En cas de contacte amb els ulls..." />
        </div>
        <div>
          <Label htmlFor="p-storage">Emmagatzematge</Label>
          <Textarea id="p-storage" rows={2} value={editing.storage} onChange={updateField("storage")} placeholder="Lloc sec, lluny de la calor..." />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="p-expiry">Caducitat</Label>
          <Input id="p-expiry" type="date" value={editing.expiry_date} onChange={updateField("expiry_date")} />
        </div>
        <div>
          <Label htmlFor="p-obs">Observacions</Label>
          <Input id="p-obs" value={editing.observations} onChange={updateField("observations")} placeholder="Notes internes" />
        </div>
      </div>

      <div>
        <Label>Fitxa tècnica oficial (PDF)</Label>
        <div className="flex items-center gap-2 mt-1">
          <input
            type="file"
            accept="application/pdf"
            id="product-pdf-upload"
            onChange={(e) => handleUploadPDF(e.target.files?.[0])}
            className="hidden"
            data-testid="product-pdf-input"
          />
          <label htmlFor="product-pdf-upload">
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              asChild
              className="cursor-pointer rounded-xl"
            >
              <span>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Paperclip className="w-4 h-4 mr-2" />}
                {editing.pdf_url ? "Reemplaçar PDF" : "Pujar PDF"}
              </span>
            </Button>
          </label>
          {editing.pdf_url && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-emerald-600" />
              <span className="text-gray-700 truncate max-w-[200px]">{editing.pdf_name || "fitxa.pdf"}</span>
              <button
                type="button"
                onClick={() => setEditing((prev) => ({ ...prev, pdf_url: "", pdf_name: "" }))}
                className="text-red-500 hover:text-red-700"
                title="Eliminar PDF"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">Màxim 5 MB. Només PDF.</p>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#7B2D3F]" />
              Fitxes tècniques de productes de neteja
            </DialogTitle>
            <DialogDescription>
              {editing ? (editing.cleaning_product_id || editing.id ? "Edita la fitxa del producte" : "Afegeix una nova fitxa de producte") : "Catàleg de productes utilitzats en les tasques de neteja"}
            </DialogDescription>
          </DialogHeader>

          {editing ? renderForm() : renderList()}

          <DialogFooter>
            {editing ? (
              <>
                <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                  Cancel·lar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
                  data-testid="save-product-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Desar
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Tancar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar producte?</AlertDialogTitle>
            <AlertDialogDescription>
              Estàs segur que vols eliminar la fitxa de <strong>{deleteTarget?.name}</strong>? Aquesta acció no es pot desfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-product">Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-product">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
